import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Sliders, Search } from 'lucide-react';

import { MasterLookupBar }    from '@/components/master/MasterLookupBar';
import { MasterPageHeader }   from '@/components/master/MasterPageHeader';
import { MasterFlash }        from '@/components/master/MasterFlash';
import { MasterAuditTab }     from '@/components/master/MasterAuditTab';
import { MasterToolbar }       from '@/components/master/MasterToolbar';
import { MasterSearchModal }   from '@/components/master/MasterSearchModal';
import type { SearchColumn }   from '@/components/master/MasterSearchModal';
import { ConfirmDialog }       from '@/components/ui/ConfirmDialog';
import { Input }               from '@/components/ui/Input';
import { Badge }               from '@/components/ui/Badge';
import { Toggle } from '@/components/ui/Toggle';
import { Button } from '@/components/ui/Button';
import { Tabs } from '@/components/ui/Tabs';
import { Combobox } from '@/components/ui/Combobox';
import { Textarea } from '@/components/ui/Textarea';
import { Card }                from '@/components/ui/Card';

import { usePermission }              from '@/hooks/usePermission';
import { useFlashMessage }    from '@/hooks/useFlashMessage';
import { useMasterScreenState }       from '@/hooks/useMasterScreenState';
import { Permissions }                from '@/constants/permissions';
import { AppRoutes }                  from '@/constants/routes';
import { reconciliationToleranceApi } from '@/api/reconciliationTolerance.api';
import type { ReconciliationToleranceDto, ReconciliationToleranceListDto, ToleranceType } from '@/types/reconciliationTolerance.types';
import type { RootState } from '@/state/store';
import { BankLookup } from '@/components/lookups/EntityLookups';

// ── Zod schema ─────────────────────────────────────────────────────────────────

const TOLERANCE_TYPES: ToleranceType[] = ['CASH', 'CARD', 'BANK'];

const schema = z.object({
  toleranceType:                  z.enum(['CASH', 'CARD', 'BANK']),
  currencyCode:                   z.string().max(10).nullable().optional(),
  bankId:                         z.string().nullable().optional(),
  toleranceAmount:                z.coerce.number().min(0, 'Must be >= 0'),
  isActive:                       z.boolean(),
  tolerancePercent:               z.coerce.number().min(0).nullable().optional(),
  requiresApprovalAboveTolerance: z.boolean(),
  approvalThresholdAmount:        z.coerce.number().min(0).nullable().optional(),
  autoPostVarianceEntry:          z.boolean(),
  varianceGlAccountCode:          z.string().max(50).nullable().optional(),
  remarks:                        z.string().max(500).nullable().optional(),
});

type FormValues = z.infer<typeof schema>;

const EMPTY_FORM: FormValues = {
  toleranceType: 'CASH', currencyCode: null, bankId: null,
  toleranceAmount: 0, isActive: true,
  tolerancePercent: null, requiresApprovalAboveTolerance: false,
  approvalThresholdAmount: null, autoPostVarianceEntry: false,
  varianceGlAccountCode: null, remarks: null,
};

const TABS = ['General', 'Audit'] as const;
type Tab = (typeof TABS)[number];

const SEARCH_COLUMNS: SearchColumn<ReconciliationToleranceListDto>[] = [
  { key: 'toleranceType',   label: 'Type',      className: 'w-20' },
  { key: 'currencyCode',    label: 'Currency',  className: 'w-24', render: r => r.currencyCode ?? '—' },
  { key: 'bankId',          label: 'Bank',      className: 'w-28', render: r => r.bankId ?? '—' },
  { key: 'toleranceAmount', label: 'Tolerance', className: 'w-28',
    render: r => r.toleranceAmount.toFixed(2) },
  { key: 'isActive',        label: 'Status',    className: 'w-20',
    render: r => <Badge variant={r.isActive ? 'success' : 'default'}>{r.isActive ? 'Active' : 'Inactive'}</Badge> },
];

function dtoToForm(dto: ReconciliationToleranceDto): FormValues {
  return {
    toleranceType: dto.toleranceType,
    currencyCode: dto.currencyCode ?? null,
    bankId: dto.bankId ?? null,
    toleranceAmount: dto.toleranceAmount,
    isActive: dto.isActive,
    tolerancePercent: dto.tolerancePercent ?? null,
    requiresApprovalAboveTolerance: dto.requiresApprovalAboveTolerance,
    approvalThresholdAmount: dto.approvalThresholdAmount ?? null,
    autoPostVarianceEntry: dto.autoPostVarianceEntry,
    varianceGlAccountCode: dto.varianceGlAccountCode ?? null,
    remarks: dto.remarks ?? null,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

export const ReconciliationTolerancePage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const companyId = useSelector((s: RootState) => s.session.session?.companyId) ?? '';

  const canCreate = hasPermission(Permissions.RECON_TOLERANCE_CREATE);
  const canEdit   = hasPermission(Permissions.RECON_TOLERANCE_EDIT);
  const canDelete = hasPermission(Permissions.RECON_TOLERANCE_DELETE);

  const screen = useMasterScreenState({ canEdit, canDelete });

  const { flashMsg, flash } = useFlashMessage();

  const [record,       setRecord]       = useState<ReconciliationToleranceDto | null>(null);
  const [activeTab,    setActiveTab]    = useState<Tab>('General');
  const [searchOpen,   setSearchOpen]   = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [lookupBusy,   setLookupBusy]   = useState(false);

  const [pendingSave,       setPendingSave]       = useState<FormValues | null>(null);
  const [showSaveConfirm,   setShowSaveConfirm]   = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const {
    register, handleSubmit, control, reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY_FORM });

  const resetToInitial = useCallback(() => {
    setRecord(null); reset(EMPTY_FORM);
    screen.setMode('initial'); setActiveTab('General'); setPendingSave(null);
  }, [reset, screen]);

  const loadRecord = useCallback((dto: ReconciliationToleranceDto) => {
    setRecord(dto); reset(dtoToForm(dto));
    screen.setMode('view'); setActiveTab('General');
  }, [reset, screen]);

  const handleEdit      = () => screen.setMode('edit');
  const handleRefresh   = () => resetToInitial();
  const handleSaveClick = () => { handleSubmit(v => { setPendingSave(v); setShowSaveConfirm(true); })(); };

  const executeSave = async () => {
    if (!pendingSave) return;
    setShowSaveConfirm(false); setSaving(true);
    const n = (v: string | null | undefined) => v?.trim() || null;
    try {
      const d = pendingSave;
      if (screen.isNew) {
        await reconciliationToleranceApi.create({
          companyId,
          toleranceType: d.toleranceType,
          currencyCode: n(d.currencyCode),
          bankId: n(d.bankId),
          toleranceAmount: d.toleranceAmount,
        });
        flash('success', 'Reconciliation tolerance created successfully.');
        resetToInitial();
      } else if (screen.isEdit && record) {
        await reconciliationToleranceApi.update(record.id, {
          toleranceType: d.toleranceType,
          currencyCode: n(d.currencyCode),
          bankId: n(d.bankId),
          toleranceAmount: d.toleranceAmount,
          isActive: d.isActive,
          tolerancePercent: d.tolerancePercent ?? null,
          requiresApprovalAboveTolerance: d.requiresApprovalAboveTolerance,
          approvalThresholdAmount: d.approvalThresholdAmount ?? null,
          autoPostVarianceEntry: d.autoPostVarianceEntry,
          varianceGlAccountCode: n(d.varianceGlAccountCode),
          remarks: n(d.remarks),
        });
        flash('success', 'Reconciliation tolerance updated successfully.');
        resetToInitial();
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Save failed.';
      flash('error', msg);
    } finally { setSaving(false); setPendingSave(null); }
  };

  const handleDelete = () => setShowDeleteConfirm(true);
  const executeDelete = async () => {
    if (!record) return;
    setShowDeleteConfirm(false); setSaving(true);
    try {
      await reconciliationToleranceApi.remove(record.id);
      flash('success', 'Tolerance record deleted.'); resetToInitial();
    } catch { flash('error', 'Delete failed.'); }
    finally { setSaving(false); }
  };

  const handleExit = () => navigate(AppRoutes.DASHBOARD);

  const handleNewRecord = () => {
    if (!canCreate) { flash('error', 'No permission to create.'); return; }
    reset(EMPTY_FORM); setRecord(null);
    screen.setMode('new'); setActiveTab('General');
  };

  const fetchItems = useCallback(async (search: string, page: number, pageSize: number) => {
    const res = await reconciliationToleranceApi.getList({ companyId, search, pageNumber: page, pageSize });
    const d = res.data.data;
    return { items: d?.items ?? [], totalCount: d?.totalCount ?? 0 };
  }, [companyId]);

  const handleSearchSelect = (item: ReconciliationToleranceListDto) => {
    setSearchOpen(false); setLookupBusy(true);
    reconciliationToleranceApi.getById(item.id)
      .then(res => { if (res.data.data) loadRecord(res.data.data); })
      .catch(() => flash('error', 'Failed to load record.'))
      .finally(() => setLookupBusy(false));
  };

  const fieldsDisabled = screen.isInitial || screen.isView;

  /** Binds a boolean field to the shared Toggle. */
  const ToggleField = ({ name, label, description }: { name: 'isActive' | 'requiresApprovalAboveTolerance' | 'autoPostVarianceEntry'; label: string; description?: string }) => (
    <Controller name={name} control={control} render={({ field }) => (
      <Toggle
        label={label}
        description={description}
        checked={field.value as boolean}
        onChange={field.onChange}
        disabled={fieldsDisabled}
      />
    )} />
  );

  // ── Render ─────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full min-h-0">
      <MasterPageHeader icon={Sliders} title="Reconciliation Tolerance Setup"
        subtitle="Configure acceptable variance thresholds for reconciliation" screen={screen} />

      <MasterToolbar
        {...screen.toolbar} saving={saving} loading={lookupBusy}
        onSave={handleSaveClick} onEdit={handleEdit}
        onDelete={handleDelete} onRefresh={handleRefresh} onExit={handleExit}
      />

      <MasterFlash message={flashMsg} />

      <MasterLookupBar
        label="Tolerance"
        summary={record && (<>
            <Badge variant="info">{record.toleranceType}</Badge>
            {record.currencyCode && <span className="text-sm font-mono text-gray-600 dark:text-gray-300">{record.currencyCode}</span>}
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Tolerance: {record.toleranceAmount.toFixed(2)}</span>
            {!record.isActive && (
              <span className="px-1.5 py-0.5 rounded bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 text-[10px] font-bold">Inactive</span>
            )}
        </>)}
      >
        <Button
          variant="outline"
          size="sm"
          leftIcon={<Search size={13} />}
          loading={lookupBusy}
          onClick={() => setSearchOpen(true)}
          disabled={screen.isNew || screen.isEdit || saving}
        >
          Browse
        </Button>
        {screen.isInitial && canCreate && (
          <Button variant="outline" size="sm" leftIcon={<Plus size={13} />} onClick={handleNewRecord}>
            New
          </Button>
        )}
      </MasterLookupBar>

      <div className="flex-1 overflow-auto">
        <form onSubmit={e => e.preventDefault()} noValidate>
          <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

          <div className="p-5 space-y-5 max-w-2xl">

            {activeTab === 'General' && (
              <>
                <Card>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">Tolerance Details</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Controller name="toleranceType" control={control} render={({ field }) => (
                        <Combobox
                          label="Tolerance Type *"
                          options={TOLERANCE_TYPES.map(o => ({ value: o, label: o }))}
                          clearable={false}
                          value={field.value}
                          onChange={field.onChange}
                          disabled={fieldsDisabled || screen.isEdit}
                        />
                      )} />
                    </div>
                    <Input label="Currency Code" {...register('currencyCode')} disabled={fieldsDisabled}
                      placeholder="e.g. LKR (optional)" className="uppercase" />
                    <Controller name="bankId" control={control}
                      render={({ field }) => (
                        <BankLookup label="Bank" value={field.value} onChange={field.onChange}
                          disabled={fieldsDisabled}
                          placeholder="Any bank"
                          hint="Leave blank to apply this tolerance to every bank." />
                      )}
                    />
                    <Input label="Tolerance Amount *" type="number" step="0.01" {...register('toleranceAmount')}
                      error={errors.toleranceAmount?.message} disabled={fieldsDisabled} />
                    <Input label="Tolerance %" type="number" step="0.01" {...register('tolerancePercent')}
                      disabled={fieldsDisabled} placeholder="Optional %" />
                    <Input label="Approval Threshold" type="number" step="0.01" {...register('approvalThresholdAmount')}
                      disabled={fieldsDisabled} placeholder="Optional" />
                    <Input label="Variance GL Account" {...register('varianceGlAccountCode')}
                      disabled={fieldsDisabled} placeholder="e.g. 9999" />
                    {(screen.isEdit || screen.isView) && (
                      <div className="sm:col-span-2">
                        <ToggleField name="isActive" label="Is Active" />
                      </div>
                    )}
                    <div className="sm:col-span-2">
                      <Textarea label="Remarks" {...register('remarks')} disabled={fieldsDisabled} rows={3} maxLength={500} placeholder="Optional notes…" />
                    </div>
                  </div>
                </Card>
                <Card>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">Approval &amp; Variance Posting</h3>
                  <div className="space-y-2">
                    <ToggleField name="requiresApprovalAboveTolerance" label="Requires Approval Above Tolerance"
                      description="Variances exceeding the threshold amount need approval." />
                    <ToggleField name="autoPostVarianceEntry" label="Auto-Post Variance Entry"
                      description="Automatically post a GL journal for approved variance amounts." />
                  </div>
                </Card>
              </>
            )}

            {activeTab === 'Audit' && <MasterAuditTab record={record} />}
          </div>
        </form>
      </div>

      <MasterSearchModal<ReconciliationToleranceListDto>
        isOpen={searchOpen} onClose={() => setSearchOpen(false)}
        title="Browse Reconciliation Tolerances" columns={SEARCH_COLUMNS}
        fetchData={fetchItems} onSelect={handleSearchSelect}
      />

      <ConfirmDialog isOpen={showSaveConfirm} variant="save"
        title={screen.isNew ? 'Save New Tolerance' : 'Save Changes'}
        message={screen.isNew ? 'Save this new tolerance rule?' : 'Save changes to this tolerance rule?'}
        confirmLabel="Yes, Save" cancelLabel="No" loading={saving}
        onConfirm={executeSave} onCancel={() => { setShowSaveConfirm(false); setPendingSave(null); }}
      />

      <ConfirmDialog isOpen={showDeleteConfirm} variant="delete"
        title="Delete Tolerance Rule"
        message={<span>Delete this tolerance record?<br /><span className="text-xs text-gray-400">This action cannot be undone.</span></span>}
        confirmLabel="Yes, Delete" cancelLabel="No" loading={saving}
        onConfirm={executeDelete} onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
};
